import classNames from "classnames";
import { SidebarContainer } from "../SidebarContainer";
import { ConstrainedTabs } from "./ConstrainedTabs";

const DEFAULT_PAGE_SPLIT = {
  resizableSidebar: true,
  enableChatOffset: true,
  defaultSidebarSplitSizes: [76, 24],
  useViewportHeight: false,
  className: "flex-1 min-h-0",
};

/**
 * @typedef {'page' | 'panel'} DetailSidebarLayoutMode
 *
 * @typedef {Object} DetailSidebarLayoutProps
 * @property {DetailSidebarLayoutMode} [layout]
 * @property {Array} tabs
 * @property {string} [error]
 * @property {string} [contentClassName]
 * @property {string} [splitStorageKey]
 * @property {import('react').ReactNode} [sidebarTitle]
 * @property {boolean} [hideSidebar]
 * @property {Object} [tabsProps]
 */

/**
 * Reusable detail layouts with a main tab region and optional right activity sidebar.
 *
 * - `page`: SidebarContainer split (Job Show, similar entity pages)
 * - `panel`: Fixed drawer body with tabs only (Dispatch event modal)
 *
 * @param {DetailSidebarLayoutProps & Record<string, unknown>} props
 */
export const DetailSidebarLayout = ({
  layout = "page",
  tabs,
  error,
  contentClassName,
  splitStorageKey,
  sidebarTitle = "Activity",
  hideSidebar,
  tabsProps = {},
  ...sidebarProps
}) => {
  const renderTabs = (dimensions = {}) => (
    <ConstrainedTabs
      layout={layout === "panel" ? "panel" : "page"}
      data={tabs}
      style={dimensions.wrapperStyle}
      panelStyle={dimensions.panelStyle}
      {...tabsProps}
    />
  );

  if (layout === "panel") {
    return (
      <div className="flex flex-col flex-1 overflow-hidden min-h-0">
        <div
          className={classNames(
            "flex-1 overflow-hidden min-h-0",
            contentClassName,
          )}
        >
          {renderTabs()}
          {error ? (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <SidebarContainer
      hideSidebar={hideSidebar}
      splitStorageKey={splitStorageKey}
      sidebarTitle={sidebarTitle}
      {...DEFAULT_PAGE_SPLIT}
      {...sidebarProps}
    >
      {(dimensions) => renderTabs(dimensions)}
    </SidebarContainer>
  );
};

export { ConstrainedTabs } from "./ConstrainedTabs";
export default DetailSidebarLayout;
